// useUnsavedChangesGuard - warns the user before they navigate away from a
// page that has unsaved changes, and lets them confirm (leave, discarding the
// changes) or cancel (stay put).
//
// Why this exists: React Router's `useBlocker`/`usePrompt` would be the
// obvious tool for this, but both require a *data* router (one created with
// `createBrowserRouter`) -- this app renders a plain `<BrowserRouter>`
// (see src/App.jsx), so those hooks throw if used here. There's no other
// "confirm before leaving" mechanism anywhere else in this codebase to reuse.
//
// How it works: every in-app navigation -- `<Link>`/`<NavLink>` clicks (the
// top nav bar) and programmatic `navigate()` calls (e.g. ItemBreadcrumbs,
// which navigates via an onClick handler rather than a real `<a href>`, so a
// raw DOM click-listener approach wouldn't catch it) -- ultimately calls
// `.push()`/`.replace()` on the single `navigator` object React Router
// shares through `UNSAFE_NavigationContext`. While this hook is mounted and
// `isDirty` is true, it temporarily wraps those two methods so the
// navigation is captured instead of performed immediately; the caller shows
// a confirmation (e.g. the shared `<Confirm>` component) and either calls
// `confirmLeave()` (performs the captured navigation) or `cancelLeave()`
// (drops it -- the user stays on the page). The original methods are
// restored on unmount.
//
// It also listens for `beforeunload` while dirty, which covers tab close /
// page refresh / typing a new URL -- the browser's own (non-customizable)
// confirmation dialog. The matching warning for autosaves that haven't
// landed yet lives in `usePendingSavesUnloadWarning` below, mounted once
// globally rather than per page.
//
// Autosave note: pages now pass `isDirty` only for states the user could
// actually lose -- a held-invalid edit or a failed save. Ordinary pending
// edits flush on unmount and finish through the module-level queue, so they
// no longer guard in-app navigation.
//
// Known limitation: browser Back/Forward button presses don't go through
// `navigator.push`/`.replace()` (they're driven by the browser's own
// history, not a call this hook can intercept), so they aren't guarded here.
//
// Usage:
//   const { promptOpen, confirmLeave, cancelLeave, markClean } = useUnsavedChangesGuard(isDirty);
//   <Confirm show={promptOpen} onConfirm={confirmLeave} onHide={cancelLeave} .../>
//
// `markClean()` is an escape hatch for callers that clear `isDirty` and then
// immediately (synchronously, in the same event handler) navigate away --
// e.g. after a delete, which discards any unsaved edits along with the
// entry itself. React state updates (like the `setDirty(false)` that flips
// the `isDirty` prop) don't take effect until the next render, so without
// this the guard would still see the *old* dirty value and needlessly
// intercept that same-tick navigation. `markClean()` flips the internal
// tracking synchronously so the very next navigation goes through.

import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { saveQueue } from './SaveQueue.mjs';

// Browser prompt on tab close / refresh / typed-URL navigation while the
// shared save queue still has pending or in-flight writes.
//
// Mounted ONCE, globally (App.jsx) rather than folded into the guard above:
// most edit surfaces are modals on pages that never call
// `useUnsavedChangesGuard` at all, and under autosave there's no un-clicked
// Save button to tell the user their edit hasn't landed. SaveQueue's
// `visibilitychange` / `pagehide` flush is best-effort -- it can start the
// write but can't wait for it -- so this prompt is what actually protects
// the edit.
export function usePendingSavesUnloadWarning() {
  useEffect(() => {
    function handleBeforeUnload(event) {
      if (saveQueue.pendingCount() === 0) return undefined;
      event.preventDefault();
      // Older browsers require `returnValue` to be set to show the prompt.
      event.returnValue = '';
      return '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
}

export function useUnsavedChangesGuard(isDirty) {
  // `useContext` never returns null here in practice -- every page that needs
  // this guard renders under the app's <BrowserRouter> -- but tests (or a
  // future caller) rendered outside a Router shouldn't crash; just no-op.
  const navigationContext = useContext(NavigationContext);
  const navigator = navigationContext?.navigator;

  // Read from a ref inside the wrapped push/replace so the wrapping effect
  // (below) doesn't need to re-run -- and re-wrap -- every time `isDirty`
  // flips.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // The navigation that got paused, so the caller can either perform it
  // (confirmLeave) or drop it (cancelLeave). Shape: { method, args }, where
  // method is 'push' or 'replace'.
  const [pending, setPending] = useState(null);
  const originalsRef = useRef(null);

  // Native browser prompt for tab close / refresh / typed-URL navigation.
  // Only this hook's own `isDirty` -- pending autosaves are covered globally
  // by usePendingSavesUnloadWarning, for every page rather than just the
  // three that mount this guard.
  useEffect(() => {
    function handleBeforeUnload(event) {
      if (!isDirtyRef.current) return undefined;
      event.preventDefault();
      // Older browsers require `returnValue` to be set to show the prompt.
      event.returnValue = '';
      return '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // In-app navigation guard: wrap the shared navigator's push/replace while
  // this hook is mounted.
  useEffect(() => {
    if (!navigator) return undefined;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    originalsRef.current = { push: originalPush, replace: originalReplace };

    navigator.push = (...args) => {
      if (isDirtyRef.current) {
        setPending({ method: 'push', args });
        return;
      }
      originalPush.apply(navigator, args);
    };
    navigator.replace = (...args) => {
      if (isDirtyRef.current) {
        setPending({ method: 'replace', args });
        return;
      }
      originalReplace.apply(navigator, args);
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator]);

  const confirmLeave = useCallback(() => {
    if (!pending || !originalsRef.current) return;
    const { method, args } = pending;
    setPending(null);
    originalsRef.current[method].apply(navigator, args);
  }, [pending, navigator]);

  const cancelLeave = useCallback(() => {
    setPending(null);
  }, []);

  // See the doc comment above for why this exists.
  const markClean = useCallback(() => {
    isDirtyRef.current = false;
  }, []);

  return {
    promptOpen: pending !== null,
    confirmLeave,
    cancelLeave,
    markClean,
  };
}

export default useUnsavedChangesGuard;
